import {API, getGraphQLPath} from '@/lib/utils/api.utils';
import {
	EULA_ACCEPTED_QUERY,
	EULA_ACCEPT_MUTATION,
	EULA_LATEST_QUERY,
	EULA_VERSIONED_QUERY
} from './eula.query';
import {formatEula, formatLocale} from '@/lib/utils/eula.utils';

export interface EulaRes {
	body: string;
	title: string;
	key: string;
	version: number;
	lastModifiedTimestamp: string;
	locale: string;
}

export interface EulaLatestQueryRes {
	data: {
		Eula: {
			getLatestAndConvert: EulaRes;
		};
	};
}

export interface EulaVersionQueryRes {
	data: {
		Eula: {
			getEulaVersion: EulaRes;
		};
	};
}

export interface EulaAcceptedRes {
	accepted?: boolean;
	key?: string;
	locale?: string;
	version?: number;
}

interface EulaAcceptedQueryRes {
	data: {
		Eula: {
			hasAccountAccepted: EulaAcceptedRes;
		};
	};
}

export interface EulaAcceptRes {
	accepted: boolean;
	version: number;
}

interface EulaAcceptMutationRes {
	data: {
		Eula: {
			acceptEula: EulaAcceptRes;
		};
	};
}

export interface EulaAcceptQueryProps {
	key: string;
	locale: string;
	accountId: string;
	version: number;
}

export type EulaAcceptedQueryProps = Omit<EulaAcceptQueryProps, 'version'>;

export const createEulaKey = (request: EulaAcceptQueryProps | EulaAcceptedQueryProps): string => {
	return JSON.stringify(request);
};

export const getLatestEula = async (key: string, locale = 'en'): Promise<EulaRes> => {
	const response = await API.post<EulaLatestQueryRes>(
		getGraphQLPath(),
		JSON.stringify({query: EULA_LATEST_QUERY(key, formatLocale(locale))}),
		{
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json'
			}
		}
	);
	return formatEula(response?.data?.data?.Eula?.getLatestAndConvert || {});
};

export const getEulaVersion = async (
	key: string,
	version: number,
	locale = 'en'
): Promise<EulaRes> => {
	const response = await API.post<EulaVersionQueryRes>(
		getGraphQLPath(),
		JSON.stringify({
			query: EULA_VERSIONED_QUERY(key, version, formatLocale(locale))
		}),
		{
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json'
			}
		}
	);
	return formatEula(response?.data?.data?.Eula?.getEulaVersion || {});
};

export const getEulaAccepted = async ({
	key,
	accountId,
	locale
}: EulaAcceptedQueryProps): Promise<EulaAcceptedRes> => {
	const response = await API.post<EulaAcceptedQueryRes>(
		getGraphQLPath(),
		JSON.stringify({
			query: EULA_ACCEPTED_QUERY(key, formatLocale(locale), accountId)
		}),
		{
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				'Cache-Control': 'no-store, no-cache',
				Expires: '0'
			}
		}
	);
	return response.data?.data?.Eula?.hasAccountAccepted || {};
};

export const acceptEula = async ({
	key,
	accountId,
	version,
	locale
}: EulaAcceptQueryProps): Promise<EulaAcceptRes> => {
	const response = await API.post<EulaAcceptMutationRes>(
		getGraphQLPath(),
		JSON.stringify({
			query: EULA_ACCEPT_MUTATION(key, formatLocale(locale), accountId, version)
		}),
		{
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				'Cache-Control': 'no-store, no-cache',
				Expires: '0'
			}
		}
	);
	return response.data?.data?.Eula?.acceptEula || {};
};
